// Charts for The Bull Sheet. Newsprint surface, print-legible, grounded ONLY in
// real firm/market data passed in — never illustrative.
//
// COLOR: every form here has the same job — polarity (good/bad, up/down,
// supports/contradicts) — so the whole page runs on ONE validated diverging
// pair plus a neutral midpoint. Validated with the dataviz palette checker
// against the cream surface (#f5f1e6, all-pairs):
//   #8a3324 ↔ #26689e  CVD ΔE 18.2 (deutan), normal ΔE 21.6, contrast ≥3:1 — PASS
// The obvious red/green choice FAILED: #8a3324↔#1f6f3f is ΔE 4.2 for
// deuteranopes — indistinguishable to ~6% of men. Do not "fix" this back to
// green without re-running the validator.
export const VIZ = {
  surface: "#f5f1e6",
  bad: "#8a3324",     // warm pole — losing, falling, contradicted
  good: "#26689e",    // cool pole — winning, rising, supported
  neutral: "#9a9384", // midpoint — flat, no bearing (gray by design)
  grid: "#cfc6b0",    // hairline, one step off surface
  ink: "#1a1712",
  inkMuted: "#6b6355",
};

const fmtPct = (n: number) => `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;

/* ─────────────────────────────────────────────────────────────────────────────
   1. POSITION LADDER — a METER, not a bar chart: one value against two limits.
   Answers the only question that matters on an open position: how much room is
   left before the stop, and how far to the target.
   ──────────────────────────────────────────────────────────────────────────── */
export function PositionLadder({
  entry, price, stop, target, side = "long", decimals = 2,
}: {
  entry: number; price: number; stop?: number | null; target?: number | null;
  side?: string; decimals?: number;
}) {
  // Need at least one bound to have a scale worth drawing.
  if (!stop && !target) return null;
  const short = side === "short";

  // Domain spans every real level, padded so end marks aren't clipped.
  const levels = [entry, price, stop, target].filter((v): v is number => typeof v === "number");
  const lo = Math.min(...levels), hi = Math.max(...levels);
  const pad = (hi - lo || Math.abs(hi) * 0.01 || 1) * 0.12;
  const d0 = lo - pad, d1 = hi + pad;
  // Three label rows, deliberately: entry above the track, the bounds directly
  // below it, and NOW on its own row beneath. A position hugging its stop puts
  // the now-marker within a few px of the stop tick, so sharing a baseline
  // would collide exactly when the chart matters most.
  const W = 320, H = 58, padX = 6, trackY = 22, trackH = 9;
  const boundLabelY = trackY + trackH + 11;
  const nowLabelY = trackY + trackH + 23;
  const x = (v: number) => padX + ((v - d0) / (d1 - d0 || 1)) * (W - 2 * padX);

  // For a long, price BELOW entry is the losing side; mirrored for a short.
  const losing = short ? price > entry : price < entry;
  const nowColor = losing ? VIZ.bad : VIZ.good;
  const fmt = (v: number) => v.toFixed(decimals);

  return (
    <figure className="m-0">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, display: "block" }}
           role="img"
           aria-label={`Position ladder: entry ${fmt(entry)}, now ${fmt(price)}`
             + (stop ? `, stop ${fmt(stop)}` : "") + (target ? `, target ${fmt(target)}` : "")}>
        {/* track: recessive, hairline-bounded */}
        <rect x={padX} y={trackY} width={W - 2 * padX} height={trackH} fill="#e6dfcc" rx={2} />

        {/* the danger zone: from the stop toward the losing side */}
        {stop != null && (
          <rect x={short ? x(stop) : padX} y={trackY}
                width={Math.max(0, short ? W - padX - x(stop) : x(stop) - padX)}
                height={trackH} fill={VIZ.bad} opacity={0.16} />
        )}
        {/* the paying zone: from the target toward the winning side */}
        {target != null && (
          <rect x={short ? padX : x(target)} y={trackY}
                width={Math.max(0, short ? x(target) - padX : W - padX - x(target))}
                height={trackH} fill={VIZ.good} opacity={0.16} />
        )}

        {/* entry — the reference line, deliberately ink not a series color */}
        <line x1={x(entry)} y1={trackY - 4} x2={x(entry)} y2={trackY + trackH + 4}
              stroke={VIZ.ink} strokeWidth={1.5} />
        <text x={x(entry)} y={trackY - 7} fontSize="7.5" textAnchor="middle"
              fill={VIZ.inkMuted} className="font-mono">entry {fmt(entry)}</text>

        {/* bounds, hairline + direct-labelled */}
        {stop != null && <Bound x={x(stop)} y={trackY} h={trackH} labelY={boundLabelY}
                                c={VIZ.bad} label={`stop ${fmt(stop)}`} />}
        {target != null && <Bound x={x(target)} y={trackY} h={trackH} labelY={boundLabelY}
                                  c={VIZ.good} label={`target ${fmt(target)}`} />}

        {/* NOW — the marker the eye should land on first (r≥4 per mark spec,
            with a surface ring so it stays legible over any zone fill) */}
        <circle cx={x(price)} cy={trackY + trackH / 2} r={5.5} fill={nowColor}
                stroke={VIZ.surface} strokeWidth={2} />
        {/* leader from marker to its own label row */}
        <line x1={x(price)} y1={trackY + trackH + 4} x2={x(price)} y2={nowLabelY - 7}
              stroke={nowColor} strokeWidth={1} opacity={0.5} />
        <text x={clamp(x(price), 22, W - 22)} y={nowLabelY} fontSize="8.5" textAnchor="middle"
              fill={VIZ.ink} className="font-mono" style={{ fontWeight: 700 }}>now {fmt(price)}</text>
      </svg>
    </figure>
  );
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function Bound({ x, y, h, labelY, c, label }: {
  x: number; y: number; h: number; labelY: number; c: string; label: string;
}) {
  return (
    <>
      <line x1={x} y1={y - 2} x2={x} y2={y + h + 2} stroke={c} strokeWidth={2} />
      {/* clamped so an end-of-domain label never clips the viewBox */}
      <text x={clamp(x, 30, 290)} y={labelY} fontSize="7.5" textAnchor="middle" fill={c}
            className="font-mono">{label}</text>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   2. CROSS-ASSET TAPE — diverging bars off a zero baseline. ONE axis (% change).
   Three horizons per asset so a one-day move can be read against the trend.
   Horizon is encoded by POSITION + label, never by a third hue.
   ──────────────────────────────────────────────────────────────────────────── */
type TapeRow = { label: string; d1?: number | null; d5?: number | null; d20?: number | null };

export function TapeChart({ rows, max = 8 }: { rows: TapeRow[]; max?: number }) {
  const shown = rows.slice(0, max);
  if (shown.length === 0) return null;
  const vals = shown.flatMap((r) => [r.d1, r.d5, r.d20].filter((v): v is number => typeof v === "number"));
  if (vals.length === 0) return null;
  const bound = Math.max(1, ...vals.map(Math.abs));

  const rowH = 34, barH = 7, gap = 2; // 2px surface gap between adjacent fills
  const W = 340, labelW = 104, padR = 34;
  const plotW = W - labelW - padR;
  const zero = labelW + plotW / 2;
  const x = (v: number) => zero + (v / bound) * (plotW / 2);
  const H = shown.length * rowH + 16;

  const spans: [keyof TapeRow, string][] = [["d1", "1d"], ["d5", "5d"], ["d20", "20d"]];

  return (
    <figure className="m-0">
      {/* legend: required for ≥2 encoded classes, and identity is never color-alone */}
      <div className="flex items-center gap-3 mb-1 text-[8.5px] uppercase tracking-widest"
           style={{ color: VIZ.inkMuted }}>
        <span>1d · 5d · 20d change</span>
        <span className="flex items-center gap-1">
          <span style={{ width: 8, height: 8, background: VIZ.good, display: "inline-block" }} /> up
        </span>
        <span className="flex items-center gap-1">
          <span style={{ width: 8, height: 8, background: VIZ.bad, display: "inline-block" }} /> down
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, display: "block" }}
           role="img" aria-label="Cross-asset tape: 1-day, 5-day and 20-day percent change per asset">
        {/* zero baseline — hairline, solid, recessive */}
        <line x1={zero} y1={4} x2={zero} y2={H - 10} stroke={VIZ.grid} strokeWidth={1} />
        {shown.map((r, i) => {
          const top = 8 + i * rowH;
          return (
            <g key={r.label}>
              <text x={labelW - 6} y={top + rowH / 2 - 1} fontSize="8.5" textAnchor="end"
                    fill={VIZ.ink} dominantBaseline="middle">{r.label}</text>
              {spans.map(([key, tag], j) => {
                const v = r[key] as number | null | undefined;
                const y = top + j * (barH + gap);
                if (typeof v !== "number") {
                  return <text key={tag} x={zero + 3} y={y + barH - 1} fontSize="6.5"
                               fill={VIZ.neutral} className="font-mono">{tag} n/a</text>;
                }
                const w = Math.abs(x(v) - zero);
                const neg = v < 0;
                return (
                  <g key={tag}>
                    {/* 4px rounded data-end, square at the baseline */}
                    <path d={roundedBar(neg ? zero - w : zero, y, w, barH, neg ? "left" : "right")}
                          fill={neg ? VIZ.bad : VIZ.good} />
                    <text x={neg ? zero - w - 3 : zero + w + 3} y={y + barH - 0.5} fontSize="6.5"
                          textAnchor={neg ? "end" : "start"} fill={VIZ.inkMuted} className="font-mono">
                      {tag} {fmtPct(v)}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </figure>
  );
}

// Bar with a 4px-rounded data-end and a square baseline end.
function roundedBar(x: number, y: number, w: number, h: number, end: "left" | "right"): string {
  const r = Math.min(4, w, h / 2);
  if (w <= 0.5) return `M${x},${y}h0v${h}h0z`;
  return end === "right"
    ? `M${x},${y}h${w - r}a${r},${r} 0 0 1 ${r},${r}v${h - 2 * r}a${r},${r} 0 0 1 ${-r},${r}h${-(w - r)}z`
    : `M${x + r},${y}h${w - r}v${h}h${-(w - r)}a${r},${r} 0 0 1 ${-r},${-r}v${-(h - 2 * r)}a${r},${r} 0 0 1 ${r},${-r}z`;
}

/* ─────────────────────────────────────────────────────────────────────────────
   3. DESK CONSENSUS — a diverging stacked bar centred on neutral. The job is an
   ordered-scale share (contradicts ↔ no bearing ↔ supports), which is exactly
   what a centred diverging stack is for. Status colour always ships with a
   count and a label, never colour alone.
   ──────────────────────────────────────────────────────────────────────────── */
export function ConsensusStrip({
  supports, contradicts, silent,
}: { supports: number; contradicts: number; silent: number }) {
  const total = supports + contradicts + silent;
  if (total === 0) return null;
  const W = 240, H = 10, gap = 2;
  const unit = (W - 2 * gap) / total;
  const wC = contradicts * unit, wS = silent * unit, wSup = supports * unit;

  return (
    <div className="flex items-center gap-2">
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ display: "block", maxWidth: "100%" }}
           role="img"
           aria-label={`Desk consensus: ${contradicts} contradict, ${silent} no bearing, ${supports} support`}>
        {contradicts > 0 && <rect x={0} y={0} width={Math.max(0, wC)} height={H} fill={VIZ.bad} rx={2} />}
        {silent > 0 && <rect x={wC + gap} y={0} width={Math.max(0, wS)} height={H} fill={VIZ.neutral} opacity={0.45} rx={2} />}
        {supports > 0 && <rect x={wC + gap + wS + gap} y={0} width={Math.max(0, wSup)} height={H} fill={VIZ.good} rx={2} />}
      </svg>
      <span className="text-[9px] uppercase tracking-widest font-mono whitespace-nowrap" style={{ color: VIZ.inkMuted }}>
        <span style={{ color: VIZ.bad }}>✗{contradicts}</span>{" · "}
        <span style={{ color: VIZ.good }}>✓{supports}</span>{" · "}
        <span>–{silent}</span>
      </span>
    </div>
  );
}
